import React, {useState, useEffect} from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import {useQuery} from '@apollo/client';
import {ORG_JOBS} from '../graphql/queries';
import {useAuth} from '../contexts/AuthContext';
import {useOrg} from '../contexts/OrgContext';
import {useDocumentTitle} from '../hooks/useDocumentTitle';
import {colors} from '../theme/colors';
import {getStageLabelForType} from '../components/GenerationProgress';

type StatusFilter = 'active' | 'completed' | 'failed' | 'all';

const STATUS_FILTER_MAP: Record<StatusFilter, string[] | undefined> = {
  active: ['QUEUED', 'RUNNING'],
  completed: ['SUCCEEDED'],
  failed: ['FAILED', 'CANCELED'],
  all: undefined,
};

function formatRelativeTime(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-US', {month: 'short', day: 'numeric'});
}

export function formatJobType(type: string): string {
  switch (type) {
    case 'GUIDE': return 'Guide';
    case 'PLAN': return 'Plan';
    case 'ANALYSIS': return 'Analysis';
    case 'GROUP_SUGGESTION': return 'Suggestions';
    case 'SOURCE_TEXT_EXTRACTION': return 'Extraction';
    case 'SOURCE_CLASSIFICATION': return 'Classification';
    case 'GROUP_GUIDE_GENERATION': return 'Group Guide';
    case 'WEB_CRAWL': return 'Web Crawl';
    case 'LIBRARY_SCAN': return 'Library Scan';
    case 'EVAL_RUN': return 'Eval Run';
    case 'EVAL_GENERATE_AND_RUN': return 'Prompt Iteration';
    case 'EVAL_MULTI_MODEL': return 'Multi-Model Eval';
    case 'EVAL_ITERATE': return 'Prompt Iteration';
    case 'EVAL_RERUN_REPORT': return 'Eval Rerun';
    default: return type;
  }
}

function statusColor(status: string): 'default' | 'info' | 'success' | 'error' | 'warning' {
  switch (status) {
    case 'QUEUED': return 'default';
    case 'RUNNING': return 'info';
    case 'SUCCEEDED': return 'success';
    case 'FAILED': return 'error';
    case 'CANCELED': return 'warning';
    default: return 'default';
  }
}

export default function JobsPage() {
  useDocumentTitle('Background Jobs');

  const {user} = useAuth();
  const {activeOrg} = useOrg();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<StatusFilter>('all');

  const statusValues = STATUS_FILTER_MAP[filter];

  const {loading, error, data, refetch} = useQuery(ORG_JOBS, {
    variables: {
      orgId: activeOrg?.id,
      ...(statusValues ? {status: statusValues} : {}),
    },
    skip: !user || !activeOrg?.id,
    fetchPolicy: 'network-only',
    pollInterval: filter === 'active' ? 5000 : 0,
  });

  const activeOrgId = activeOrg?.id;
  useEffect(() => {
    if (activeOrgId && user) {
      refetch();
    }
  }, [activeOrgId, user, refetch]);

  const jobs = data?.orgJobs || [];

  return (
    <Box sx={{maxWidth: 1040, margin: '0 auto', padding: '24px 24px 64px'}}>
      {/* Header */}
      <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3}}>
        <Box>
          <Typography sx={{fontSize: 24, fontWeight: 650, color: colors.textPrimary}}>
            Background Jobs
          </Typography>
          <Typography sx={{fontSize: 14, color: '#6B7280', mt: 0.5}}>
            Background jobs for {activeOrg?.name || 'your organization'}
          </Typography>
        </Box>
      </Box>

      {/* Filter toggles */}
      <Box sx={{mb: 2}}>
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(_, val) => val && setFilter(val)}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              px: 2,
              py: 0.5,
              textTransform: 'none',
              fontSize: 13,
              fontWeight: 500,
            },
          }}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="active">In progress</ToggleButton>
          <ToggleButton value="completed">Completed</ToggleButton>
          <ToggleButton value="failed">Failed</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* List card */}
      <Box
        sx={{
          bgcolor: '#fff',
          border: '1px solid #E5E7EB',
          borderRadius: '12px',
          boxShadow: '0 1px 2px rgba(0,0,0,.06)',
          overflow: 'hidden',
        }}
      >
        {/* Table header */}
        <Box
          sx={{
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            borderBottom: '1px solid #E5E7EB',
            bgcolor: '#FAFAFA',
          }}
        >
          {[
            {label: 'Summary', flex: 1, width: undefined},
            {label: 'Type', flex: undefined, width: 100},
            {label: 'Stage', flex: undefined, width: 180},
            {label: 'Cost', flex: undefined, width: 80},
            {label: 'Status', flex: undefined, width: 100},
            {label: 'Created', flex: undefined, width: 120},
          ].map(col => (
            <Typography
              key={col.label}
              sx={{
                flex: col.flex,
                width: col.width,
                fontSize: 12,
                fontWeight: 600,
                color: '#6B7280',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              {col.label}
            </Typography>
          ))}
        </Box>

        {/* Body */}
        {loading && (
          <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{m: 2}}>
            Error loading jobs: {error.message}
          </Alert>
        )}

        {!loading && !error && jobs.length === 0 && (
          <Box sx={{py: 6, textAlign: 'center'}}>
            <Typography sx={{fontSize: 14, color: '#9CA3AF'}}>
              No jobs found
            </Typography>
          </Box>
        )}

        {jobs.map((job: any) => (
          <Box
            key={job.id}
            onClick={() => navigate(`/org/jobs/${job.id}`)}
            sx={{
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              borderBottom: '1px solid #F3F4F6',
              cursor: 'pointer',
              '&:hover': {bgcolor: '#F9FAFB'},
              '&:last-child': {borderBottom: 'none'},
            }}
          >
            <Typography
              sx={{
                flex: 1,
                fontSize: 14,
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                pr: 2,
              }}
            >
              {job.inputSummary || 'Untitled'}
            </Typography>
            <Typography sx={{width: 100, fontSize: 13, color: '#6B7280'}}>
              {formatJobType(job.type)}
            </Typography>
            <Typography sx={{width: 180, fontSize: 13, color: '#6B7280', textTransform: 'capitalize'}}>
              {job.currentStage ? getStageLabelForType(job.currentStage, job.type) : ''}
            </Typography>
            <Typography sx={{width: 80, fontSize: 13, color: '#6B7280'}}>
              {(() => {
                const rd = job.resultData as any;
                const cost = rd?.costEstimate?.totalCost ?? rd?.totalCost;
                if (cost == null) return '—';
                return cost < 0.01 ? `~$${cost.toFixed(4)}` : `~$${cost.toFixed(2)}`;
              })()}
            </Typography>
            <Box sx={{width: 100}}>
              <Chip
                label={job.status}
                size="small"
                color={statusColor(job.status)}
                variant={job.status === 'RUNNING' ? 'filled' : 'outlined'}
                sx={{height: 22, fontSize: 11}}
              />
            </Box>
            <Typography sx={{width: 120, fontSize: 13, color: '#9CA3AF'}}>
              {formatRelativeTime(job.createdAt)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
